import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const PatientsList = ({ patients = [], onEdit, onDelete, onAddPatient, onManagePatientsProfile }) => {
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ Helper: Calculate Age from DOB
  const calculateAge = (dob) => {
    if (!dob) return "—";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // ✅ Add computed age to each patient
  const patientsWithAge = patients.map((p) => ({
    ...p,
    age: calculateAge(p.dob),
  }));

  // ✅ Filter by name, email, phone, or MR No
  const filteredPatients = patientsWithAge.filter((p) => {
    const fullName = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
    const mrNo = (p.mr_no || "").toLowerCase();
    return (
      fullName.includes(filterText.toLowerCase()) ||
      mrNo.includes(filterText.toLowerCase()) ||
      (p.email && p.email.toLowerCase().includes(filterText.toLowerCase())) ||
      (p.phone && p.phone.toLowerCase().includes(filterText.toLowerCase()))
    );
  });

  const totalRows = filteredPatients.length;
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  // ✅ Custom Pagination
  const CustomPagination = ({
    onChangePage,
    onChangeRowsPerPage,
    currentPage,
    rowsPerPage,
    rowCount,
  }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage);

    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>
            Rows per page:
          </span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px", fontSize: "0.875rem" }}
            value={rowsPerPage}
            onChange={(e) =>
              onChangeRowsPerPage(Number(e.target.value), currentPage)
            }
          >
            {[5, 10, 15, 20, 25].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-sm border"
            style={{
              borderRadius: "6px",
              height: "32px",
              width: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#495057",
              border: "1px solid #dee2e6",
              backgroundColor: "#fff",
            }}
            onClick={() => onChangePage(currentPage - 1, rowCount)}
            disabled={currentPage === 1}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>

          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className="btn btn-sm border"
            style={{
              borderRadius: "6px",
              height: "32px",
              width: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#495057",
              border: "1px solid #dee2e6",
              backgroundColor: "#fff",
            }}
            onClick={() => onChangePage(currentPage + 1, rowCount)}
            disabled={currentPage === totalPages}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  // ✅ Table Columns
  const columns = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "70px",
    },
    {
      name: "MR No",
      selector: (row) => row.mr_no || "—",
      sortable: true,
      width: "130px",
    },
    {
      name: "Name",
      selector: (row) =>
        `${row.first_name || ""} ${row.last_name || ""}`.trim(),
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email || "—",
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phone || "—",
      sortable: true,
    },
    {
      name: "Gender",
      selector: (row) => row.gender || "—",
      sortable: true,
      width: "100px",
    },
    {
      name: "Age",
      selector: (row) => row.age || "—",
      sortable: true,
      width: "80px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex justify-content-center gap-1">
          {onManagePatientsProfile && (
            <button
              className="btn btn-sm btn-outline-primary"
              title="Manage Active Client"
              onClick={() => onManagePatientsProfile(row)}
            >
              <i className="ri-user-line"></i>
            </button>
          )}
          {onEdit && (
            <button
              className="btn btn-sm btn-outline-primary"
              title="Edit Active Client"
              onClick={() => onEdit(row.id)}
            >
              <i className="ri-edit-line"></i>
            </button>
          )}
          {onDelete && (
            <button
              className="btn btn-sm btn-outline-danger"
              title="Delete Patient"
              onClick={() => onDelete(row.id)}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "130px",
    },
  ];

  // ✅ Export Handlers
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Active Clients List", 14, 10);
    const tableColumn = ["ID", "MR No", "Name", "Email", "Phone", "Gender", "Age"];
    const tableRows = [];

    filteredPatients.forEach((p) => {
      const name = `${p.first_name || ""} ${p.last_name || ""}`.trim();
      tableRows.push([
        p.id,
        p.mr_no || "—",
        name,
        p.email || "",
        p.phone || "",
        p.gender || "",
        p.age || "",
      ]);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });

    doc.save("patients_list.pdf");
  };

  const handleCopy = () => {
    const copyText = filteredPatients
      .map(
        (p) =>
          `${p.id}\t${p.mr_no || "—"}\t${p.first_name || ""} ${p.last_name || ""}\t${p.email || ""}\t${p.phone || ""}`
      )
      .join("\n");

    navigator.clipboard.writeText(copyText);

    Swal.fire({
      title: "Copied!",
      text: "Patient data copied to clipboard.",
      icon: "success",
      timer: 1500,
      showConfirmButton: false,
      position: "top",
      width: "280px",
    });
  };

  const handlePrint = () => {
    const printContent = `
      <html>
      <head>
        <title>Active Clients List</title>
        <style>
          body { font-family: Arial; margin: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }
          th { background: #f8f9fa; }
        </style>
      </head>
      <body>
        <h3>Active Clients List</h3>
        <table>
          <thead>
            <tr><th>ID</th><th>MR No</th><th>Name</th><th>Email</th><th>Phone</th><th>Gender</th><th>Age</th></tr>
          </thead>
          <tbody>
            ${filteredPatients
        .map(
          (p) => `
              <tr>
                <td>${p.id}</td>
                <td>${p.mr_no || "—"}</td>
                <td>${p.first_name || ""} ${p.last_name || ""}</td>
                <td>${p.email || ""}</td>
                <td>${p.phone || ""}</td>
                <td>${p.gender || ""}</td>
                <td>${p.age || ""}</td>
              </tr>`
        )
        .join("")}
          </tbody>
        </table>
      </body>
      </html>`;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div>
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        {/* Left: Export + Search */}
        <div className="d-flex align-items-center gap-2 position-relative">
          <div className="dropdown">
            <button
              className="btn btn-label-primary btn-sm dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li>
                <button className="dropdown-item" onClick={handleExportPDF}>
                  <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                </button>
              </li>
              <li>
                <CSVLink
                  data={filteredPatients}
                  filename="patients_export.csv"
                  className="dropdown-item"
                >
                  <i className="ri-file-excel-line me-1 text-success"></i> CSV
                </CSVLink>
              </li>
              <li>
                <button className="dropdown-item" onClick={handleCopy}>
                  <i className="ri-file-copy-line me-1"></i> Copy
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={handlePrint}>
                  <i className="ri-printer-line me-1"></i> Print
                </button>
              </li>
            </ul>
          </div>

          <input
            type="text"
            placeholder="Search patient..."
            className="form-control form-control-sm"
            style={{ width: "200px" }}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        {/* Center: Info */}
        <h6 className="mb-0 text-center text-muted flex-grow-1">
          Showing {start}-{end} of {totalRows} patients
        </h6>

        {/* Right: Add Button */}
        <div>
          {onAddPatient && (
            <button className="btn btn-primary btn-sm" onClick={onAddPatient}>
              <i className="ri-user-add-line me-1"></i> Add Active Client
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-2">
        <DataTable
          columns={columns}
          data={filteredPatients}
          pagination
          paginationPerPage={rowsPerPage}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={setRowsPerPage}
          onChangePage={setCurrentPage}
          paginationComponent={CustomPagination}
          highlightOnHover
          striped
          responsive
          noDataComponent={<div className="p-3 text-muted">No patients found</div>}
        />
      </div>
    </div>
  );
};

export default PatientsList;
